import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faTimes, faCamera, faSyncAlt, faExpand, faCompress } from '@fortawesome/free-solid-svg-icons';

const AttachFilesModal = ({
  isOpen,
  onClose,
  claim,
  files,
  setFiles,
  handleFileChange,
  handleRemoveFile,
  handleViewFile,
  handleRefreshLink,
  isDragging,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleChooseFile,
  fileInputRef,
  allowedFileTypes,
  maxFileSizeMB,
  maxUploadLimit,
}) => {
  const [isMaximized, setIsMaximized] = useState(false);
  if (!isOpen || !claim) return null;
  const safeFiles = Array.isArray(files) ? files : [];
  const attachedFiles = Array.isArray(claim?.attachedFiles)
    ? claim.attachedFiles
    : [];
  const displayFiles = safeFiles.length ? safeFiles : attachedFiles;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className={`bg-white rounded-xl w-full p-6 flex flex-col transition-all duration-200 ${
          isMaximized ? 'max-w-5xl h-[85vh]' : 'max-w-2xl'
        }`}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Attach Files for Transaction {claim.transaction_no}
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsMaximized((prev) => !prev)}
              className="text-gray-500 hover:text-gray-700"
              title={isMaximized ? 'Restore size' : 'Maximize'}
            >
              <FontAwesomeIcon icon={isMaximized ? faCompress : faExpand} />
            </button>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>
        <div className="mb-4">
          <div
            className={`border-2 border-dashed rounded-xl p-6 text-center transition-all duration-300 ${
              isDragging ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 bg-gray-50'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <FontAwesomeIcon icon={faCamera} className="text-4xl text-indigo-500 mb-4" />
            <p className="text-gray-700 text-lg">
              Drag and drop files here or{' '}
              <button
                type="button"
                onClick={handleChooseFile}
                className="text-indigo-600 font-semibold hover:text-indigo-800"
              >
                choose files manually
              </button>
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Supported formats: {allowedFileTypes.join(', ')} (Max {maxFileSizeMB}MB per file, up to {maxUploadLimit} files)
            </p>
            <input
              type="file"
              ref={fileInputRef}
              multiple
              accept={allowedFileTypes.map((ext) => `.${ext}`).join(',')}
              onChange={(e) => handleFileChange(e.target.files)}
              className="hidden"
            />
          </div>
        </div>
        {displayFiles.length > 0 && (
          <div className={`mb-4 ${isMaximized ? 'overflow-auto' : ''}`}>
            <h4 className="text-md font-semibold text-gray-800 mb-2">Selected Files</h4>
            <ul className="space-y-2">
              {displayFiles.map((fileObj, index) => (
                <li
                  key={index}
                  className="flex items-center justify-between bg-gray-100 p-3 rounded-lg"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 break-words">
                      {fileObj.file?.name ||
                        fileObj.original_file_name ||
                        fileObj.file_name ||
                        'Unknown file'}
                    </p>
                    {fileObj.isUploading && (
                      <div className="relative pt-2">
                        <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
                          <div
                            className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${fileObj.progress}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-600 mt-1 block">
                          {fileObj.progress}% Uploading
                        </span>
                      </div>
                    )}
                    {fileObj.error && (
                      <p className="text-xs text-red-600 mt-1">Error: {fileObj.error}</p>
                    )}
                    {fileObj.uploadId && (
                      <p className="text-xs text-green-600 mt-1">Uploaded (ID: {fileObj.uploadId})</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {fileObj.signed_url && (
                      <button
                        onClick={() => handleViewFile(fileObj)}
                        className="text-indigo-600 hover:text-indigo-800"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                    )}
                    {fileObj.uploadId && (
                      <button
                        onClick={() => handleRefreshLink(fileObj, "claim")}
                        className="text-blue-600 hover:text-blue-800"
                        title="Refresh link"
                      >
                        <FontAwesomeIcon icon={faSyncAlt} />
                      </button>
                    )}
                    <button
                      onClick={() => handleRemoveFile(fileObj)}
                      className="text-red-500 hover:text-red-700"
                      disabled={fileObj.isUploading}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-gray-500">
            Files are saved automatically after upload.
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AttachFilesModal;
